// Vaanmathi
import React, { useState, useEffect } from "react";
import {
  FiUsers,
  FiCalendar,
  FiClock,
  FiAlertCircle,
  FiCheckCircle,
  FiSearch,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Spinner,
  Alert,
  Form,
  Row,
  Col,
  Pagination,
  Modal,
  Button,
} from "react-bootstrap";
import { showMyAlert } from "../../Utils/alertService";

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    totalEmployees: 0,
    employeesWithPayslips: 0,
    currentPayCycle: "",
    pendingRuns: 0,
    pendingAmount: "0",
    totalPayroll: 0,
    recentPayslips: [],
    allPayrolls: [],
    pendingEmployees: [],
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [monthFilter, setMonthFilter] = useState("all");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const navigate = useNavigate();
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const companyKey = sessionStorage.getItem("companyKey");

  const [showFilters, setShowFilters] = useState(false)


  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");
      if (!userId) {
        navigate("/signIn");
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatDateForInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleOpenPdfModal = (payslip) => {
    if (payslip.payslipPdf) {
      const pdfUrl = `https://api.dremerz.net/uploads/${payslip.payslipPdf}`;
      setCurrentPdfUrl(pdfUrl);
      setShowPdfModal(true);
    } else {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "Payslip PDF Not Available",
        message: "Payslip PDF not available for this record",
        duration: 2500, // auto-close in 3s
      })
    }
  };


  const handleDownloadPdf = async () => {
    if (currentPdfUrl) {
      try {
        const response = await fetch(currentPdfUrl);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        const fileName = currentPdfUrl.split('/').pop() || 'payslip.pdf';
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('Error downloading PDF:', error);
        showMyAlert({
          type: "danger", // "success" | "danger" | "warning" | "info"
          title: "Download Failed",
          message: "Failed to download PDF",
          duration: 2500, // auto-close in 3s
        });
      }
    }
  };

  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    setCurrentPdfUrl(null);
  };

  const handleStartDateChange = (e) => {
    const value = e.target.value;
    if (value) {
      const [year, month, day] = value.split('-');
      const selectedDate = new Date(year, month - 1, day, 0, 0, 0);
      setStartDate(selectedDate);
    } else {
      setStartDate(null);
    }
    if (value) setMonthFilter("custom");
  };

  const handleEndDateChange = (e) => {
    const value = e.target.value;
    if (value) {
      const [year, month, day] = value.split('-');
      const selectedDate = new Date(year, month - 1, day, 23, 59, 59);
      setEndDate(selectedDate);
    } else {
      setEndDate(null);
    }
    if (value) setMonthFilter("custom");
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch employee details
        const empDetailsResponse = await fetch(
          "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollEmployeeBasicDetails_PayrollEmployeeDetails"
        );
        const empDetailsResData = await empDetailsResponse.json();

        const empDetailsData = empDetailsResData.filter((e) => e.companyKey === companyKey);

        // Fetch employee count
        const empResponse = await fetch(
          "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers"
        );
        const empResData = await empResponse.json();
        const empData = empResData.filter((e) => e.companyKey === companyKey);
        const employeeCount = Array.isArray(empData) ? empData.length : 1;

        // Fetch payroll data
        const payrollResponse = await fetch(
          "https://api.dremerz.net/api/DZ_8924_Payroll_PayrollSalaryCalculation_PayrollAddSalary_PayrollSalaryList"
        );
        const payrollResData = await payrollResponse.json();
        const payrollData = payrollResData.filter((p) => p.companyKey === companyKey);
        const processedPayrollData = Array.isArray(payrollData)
          ? payrollData
          : [];

        const now = new Date();
        const currentMonthStart = new Date(
          now.getFullYear(),
          now.getMonth(),
          1
        );
        const currentMonthEnd = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          0
        );

        const allPayrolls = processedPayrollData
          .filter((item) => item.startDate && item.endDate)
          .map((item) => {
            const employee = Array.isArray(empDetailsData)
              ? empDetailsData.find((emp) => emp.userMobile === item.userId)
              : null;

            return {
              id: item.userId,
              payrollId: item.id,
              name: item.userName || "Employee",
              mobile: item.mobileNumber || "N/A",
              employeeId: item.employee_id || "N/A",
              startdate: item.startDate,
              enddate: item.endDate,
              startDate: item.startDate,
              endDate: item.endDate,
              amount: parseFloat(item.overallcalculatedSalary || 0),
              formattedAmount: `₹${parseFloat(
                item.overallcalculatedSalary || 0
              ).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
              status:
                item.payslipPdf && item.payslipPdf.trim() !== ""
                  ? "Completed"
                  : "Pending",
              department: employee?.department || "N/A",
              payslipGenerated: !!(
                item.payslipPdf && item.payslipPdf.trim() !== ""
              ),
              payslipPdf: item.payslipPdf || null,
              date: item.startDate ? new Date(item.startDate) : null,
              payslipGeneratedAt: item.payslipgeneratedat
                ? new Date(item.payslipgeneratedat)
                : null,
              payslipGeneratedAtRaw: item.payslipgeneratedat || null,
            };
          });

        const currentMonthPayrolls = allPayrolls.filter((item) => {
          if (!item.startDate) return false;
          const itemStartDate = new Date(item.startDate);
          const isInCurrentMonth =
            itemStartDate >= currentMonthStart &&
            itemStartDate <= currentMonthEnd;

          return isInCurrentMonth;
        });

        const currentMonthPayroll = currentMonthPayrolls.reduce((sum, item) => {
          return sum + item.amount;
        }, 0);

        const formattedCurrentMonthPayroll = `${currentMonthPayroll.toLocaleString(
          "en-IN",
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }
        )}`;

        const totalPayroll = allPayrolls.reduce(
          (sum, item) => sum + item.amount,
          0
        );
        const formattedTotalPayroll = `${totalPayroll.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;

        const pendingPayrollRuns = currentMonthPayrolls.filter(
          (item) => !item.payslipGenerated
        );

        const pendingAmount = pendingPayrollRuns.reduce(
          (sum, item) => sum + item.amount,
          0
        );
        const formattedPendingAmount = `${pendingAmount.toLocaleString(
          "en-IN",
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }
        )}`;

        const pendingEmployees = pendingPayrollRuns.map((item) => ({
          id: item.id,
          payrollId: item.payrollId,
          name: item.name,
          employeeId: item.employeeId,
          mobile: item.mobile,
          startDate: item.startDate,
          endDate: item.endDate,
          amount: item.amount,
          formattedAmount: item.formattedAmount,
        }));

        const employeesWithGeneratedPayslips = currentMonthPayrolls.filter(
          (item) => item.payslipGenerated
        ).length;


        const recentPayslips = allPayrolls
          .filter((item) => item.payslipGenerated && item.payslipGeneratedAt)
          .sort((a, b) => {
            const dateA = a.payslipGeneratedAt;
            const dateB = b.payslipGeneratedAt;
            return dateB - dateA;
          })
          .slice(0, 5);

        setDashboardData({
          totalEmployees: employeeCount,
          employeesWithPayslips: employeesWithGeneratedPayslips,
          currentMonthPayroll: formattedCurrentMonthPayroll,
          pendingRuns: pendingPayrollRuns.length,
          pendingAmount: formattedPendingAmount,
          totalPayroll: formattedTotalPayroll,
          recentPayslips,
          allPayrolls: allPayrolls,
          pendingEmployees,
        });

      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        showMyAlert({
          type: "danger", // "success" | "danger" | "warning" | "info"
          title: "Error",
          message: "Error fetching dashboard data",
          duration: 2500, // auto-close in 3s
        });
        setDashboardData({
          totalEmployees: 0,
          employeesWithPayslips: 0,
          currentMonthPayroll: "₹0",
          pendingRuns: 0,
          pendingAmount: "₹0",
          totalPayroll: "₹0",
          recentPayslips: [],
          allPayrolls: [],
          pendingEmployees: [],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [monthFilter, startDate, endDate]);

  const calculateDaysBetween = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    if (isNaN(startDate) || isNaN(endDate)) return 0;

    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;
    return diffDays;
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return "";

    const date = new Date(dateInput);

    if (isNaN(date)) return dateInput;

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const isDateInRange = (dateToCheck, startDate, endDate) => {
    if (!dateToCheck) return false;

    const checkDate = new Date(dateToCheck);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    if (start) start.setHours(0, 0, 0, 0);
    if (end) end.setHours(23, 59, 59, 999);
    checkDate.setHours(0, 0, 0, 0);

    if (start && end) {
      return checkDate >= start && checkDate <= end;
    } else if (start) {
      return checkDate >= start;
    } else if (end) {
      return checkDate <= end;
    }

    return true;
  };


  const filteredPayslips = dashboardData.allPayrolls
    .filter((item) => item.payslipPdf)
    .filter((payslip) => {
      let matchesDateRange = true;

      if (startDate || endDate) {
        const referenceDate = payslip.payslipGeneratedAtRaw || payslip.startDate;
        matchesDateRange = isDateInRange(referenceDate, startDate, endDate);
      }
      else if (monthFilter !== "current" && monthFilter !== "all") {
        const now = new Date();
        const monthsBack = parseInt(monthFilter);
        const filterStart = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);

        const payslipDate = payslip.payslipGeneratedAtRaw
          ? new Date(payslip.payslipGeneratedAtRaw)
          : new Date(payslip.startDate);

        payslipDate.setHours(0, 0, 0, 0);
        const filterStartDate = new Date(filterStart);
        filterStartDate.setHours(0, 0, 0, 0);

        matchesDateRange = payslipDate >= filterStartDate;
      }
      else if (monthFilter === "current") {
        const now = new Date();
        const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        const referenceDate = payslip.payslipGeneratedAtRaw
          ? new Date(payslip.payslipGeneratedAtRaw)
          : new Date(payslip.startDate);

        referenceDate.setHours(0, 0, 0, 0);
        currentMonthStart.setHours(0, 0, 0, 0);
        currentMonthEnd.setHours(23, 59, 59, 999);

        matchesDateRange =
          referenceDate >= currentMonthStart && referenceDate <= currentMonthEnd;
      }

      const startDateStr = payslip.startDate ? formatDate(payslip.startDate) : "";
      const endDateStr = payslip.endDate ? formatDate(payslip.endDate) : "";
      const daysBetween = calculateDaysBetween(payslip.startDate, payslip.endDate).toString();
      const generatedAtStr = payslip.payslipGeneratedAtRaw
        ? formatDate(payslip.payslipGeneratedAtRaw)
        : "";

      const matchesSearch =
        payslip.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payslip.mobile.includes(searchTerm) ||
        payslip.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payslip.formattedAmount.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payslip.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
        startDateStr.includes(searchTerm) ||
        endDateStr.includes(searchTerm) ||
        daysBetween.includes(searchTerm) ||
        generatedAtStr.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesDateRange && matchesSearch;
    })
    .sort((a, b) => {
      if (a.payslipGeneratedAt && b.payslipGeneratedAt) {
        return b.payslipGeneratedAt - a.payslipGeneratedAt;
      }
      if (a.payslipGeneratedAt && !b.payslipGeneratedAt) return -1;
      if (!a.payslipGeneratedAt && b.payslipGeneratedAt) return 1;
      return new Date(b.startDate) - new Date(a.startDate);
    });


  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredPayslips.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );
  const totalPages = Math.ceil(filteredPayslips.length / recordsPerPage);

  const StatCard = ({
    title,
    subtitle,
    value,
    icon: Icon,
    iconColor,
    bgColor,
    isLoading,
    className,
    style,
    url
  }) => (
    <Card className={`shadow-sm h-100 ${className}`}>
      <Card.Body className="p-2 p-sm-3">
        <div role="button" onClick={() => navigate(url)} className="d-flex align-items-center justify-content-between mb-0">
          <div
            className={`p-2 p-sm-3 rounded-circle ${bgColor} d-flex align-items-center justify-content-center`}
            style={{
              width: isMobile ? "36px" : "44px",
              height: isMobile ? "36px" : "44px",
              minWidth: isMobile ? "36px" : "44px"
            }}
          >
            {isLoading ? (
              <Spinner animation="border" size="sm" className={iconColor} />
            ) : (
              <Icon className={iconColor} size={isMobile ? 16 : 18} />
            )}
          </div>
          <div className="flex-grow-1 ms-2 ms-sm-3">
            <h2
              className="fw-bold text-dark mb-1"
              style={{
                fontSize: "14px",
                lineHeight: "1.2"
              }}
            >
              {isLoading ? (
                <Spinner animation="border" size="sm" className="text-primary" />
              ) : (
                value
              )}
            </h2>
            <h6
              className="text-muted fw-medium mb-1"
              style={{ fontSize: "13px" }}
            >
              {subtitle}
            </h6>
            <h6
              className="text-muted fw-medium mb-1"
              style={{ fontSize: "13px" }}
            >
              {title}
            </h6>
          </div>
        </div>
      </Card.Body>
    </Card>
  );


  const RupeeIcon = ({ size = 24, className = "" }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M6 3h12" />
      <path d="M6 8h12" />
      <path d="M8 12h8" />
      <path d="M8 16l4-4" />
      <path d="M16 16c0-4-4-4-8-4" />
    </svg>
  );

  return (
    <div
      className="container-fluid px-2 px-sm-3 mt-5"
      style={{
        position: "relative",
        zIndex: 1,
        background: "rgba(255, 255, 255, 1)",
        minWidth: "300px",
        overflowX: "hidden"
      }}
    >
      <div className="container-xxl p-0">
        {/* Header */}

        {/* Stats Grid */}
        <div className="row g-2 g-sm-3 g-md-4 mb-3 mb-sm-3">
          <div className="col-6 col-sm-6 col-lg-3">
            <StatCard
              title="Total Employees"
              value={dashboardData.totalEmployees}
              icon={FiUsers}
              iconColor="text-primary"
              bgColor="bg-primary bg-opacity-10"
              isLoading={loading}
              className="border-0 rounded-3"
              url="/employeemanagement"
            />
          </div>

          <div className="col-6 col-sm-6 col-lg-3">
            <StatCard
              title="Current Month Payroll"
              value={dashboardData.currentMonthPayroll}
              subtitle="In Rupees"
              icon={FiCalendar}
              iconColor="text-success"
              bgColor="bg-success bg-opacity-10"
              isLoading={loading}
              className="border-0 rounded-3"
              style={{ fontSize: isMobile ? "1rem" : "2rem" }}
              url="/revenue"
            />
          </div>

          <div className="col-6 col-sm-6 col-lg-3">
            <StatCard
              title={`Pending Amount (${dashboardData.pendingRuns})`}
              value={dashboardData.pendingAmount}
              subtitle="In Rupees"
              icon={FiAlertCircle}
              iconColor={
                dashboardData.pendingRuns > 0 ? "text-warning" : "text-success"
              }
              bgColor={
                dashboardData.pendingRuns > 0
                  ? "bg-warning bg-opacity-10"
                  : "bg-success bg-opacity-10"
              }
              isLoading={loading}
              className={
                dashboardData.pendingRuns > 0
                  ? "border-0 rounded-3"
                  : "border-0 rounded-3"
              }
              url="/revenue"
            />
          </div>

          <div className="col-6 col-sm-6 col-lg-3">
            <StatCard
              title="Total Payroll"
              value={dashboardData.totalPayroll}
              subtitle="In Rupees"
              icon={RupeeIcon}
              iconColor="text-info"
              bgColor="bg-info bg-opacity-10"
              isLoading={loading}
              className="border-0 rounded-3"
              url="/revenue"
            />
          </div>
        </div>

        {isMobile && (
          <div className="d-flex justify-content-end mb-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </div>
        )}


        <div
          className={`row mb-4 ${isMobile ? (showFilters ? "d-flex" : "d-none") : "d-flex"
            }`}
        >
          <div className="col-12">
            <Card className="shadow-sm border-0 mb-3 mb-sm-4" style={{ borderRadius: "16px" }}>
              <Card.Body className="p-3 p-sm-4">
                <Row className="g-3 align-items-end">
                  <Col xs={12} lg={4}>
                    <Form.Group>
                      <small className="text-muted fw-medium d-block mb-1">Search</small>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <FiSearch />
                        </span>
                        <Form.Control
                          type="text"
                          placeholder="Search here..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="border-start-0"
                        />
                        {searchTerm && (
                          <button
                            type="button"
                            className="input-group-text bg-white border-start-0"
                            onClick={() => setSearchTerm("")}
                            style={{ cursor: "pointer" }}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} lg={3}>
                    <Form.Group>
                      <small className="text-muted fw-medium d-block mb-1">Filter by month</small>
                      <Form.Select
                        value={monthFilter}
                        onChange={(e) => {
                          setMonthFilter(e.target.value);
                          setStartDate(null);
                          setEndDate(null);
                        }}
                      >
                        <option value="all">All Time</option>
                        <option value="current">Current Month</option>
                        <option value="3">Last 3 Months</option>
                        <option value="6">Last 6 Months</option>
                        <option value="9">Last 9 Months</option>
                        <option value="12">Last 12 Months</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={6} sm={3} lg={2}>
                    <Form.Group>
                      <small className="text-muted fw-medium d-block mb-1">From date</small>
                      <Form.Control
                        type="date"
                        value={startDate ? formatDateForInput(startDate) : ""}
                        onChange={handleStartDateChange}
                        max={
                          endDate
                            ? formatDateForInput(endDate)
                            : formatDateForInput(new Date())
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={6} sm={3} lg={2}>
                    <Form.Group>
                      <small className="text-muted fw-medium d-block mb-1">To date</small>
                      <Form.Control
                        type="date"
                        value={endDate ? formatDateForInput(endDate) : ""}
                        onChange={handleEndDateChange}
                        min={startDate ? formatDateForInput(startDate) : ""}
                        max={formatDateForInput(new Date())}
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} sm={6} lg={1} className="text-lg-end text-center">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setSearchTerm("");
                        setMonthFilter("all");
                        setStartDate(null);
                        setEndDate(null);
                      }}
                      title="Clear all filters"
                      className="d-flex align-items-center justify-content-center gap-1 w-100 mb-1"
                    >
                      Clear
                    </Button>
                  </Col>
                </Row>

              </Card.Body>
            </Card>
          </div>
        </div>
        <div className="row mb-4">
          <div className="col-12">
            <Card className="shadow-sm" style={{ borderRadius: "16px" }}>
              <Card.Body className="p-3 p-sm-4">
                <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-3 mb-sm-4">
                  <div className="d-flex align-items-center mb-2 mb-sm-0">
                    <div className="p-2 bg-primary bg-opacity-10 rounded-circle me-2 me-sm-3">
                      <FiCheckCircle size={isMobile ? 18 : 20} className="text-primary" />
                    </div>
                    <h4
                      className="fw-bold text-dark mb-0"
                      style={{ fontSize: isMobile ? "1.25rem" : "1.5rem" }}
                    >
                      Generated Payslips
                    </h4>
                  </div>
                  <div className="d-flex align-items-center">
                    <span
                      className="text-muted"
                      style={{ fontSize: isMobile ? "0.8rem" : "0.9rem" }}
                    >
                      Showing {filteredPayslips.length} records
                    </span>
                  </div>
                </div>

                {currentRecords.length > 0 ? (
                  <>
                    <div className={isMobile ? "" : "row g-3 mb-4"}>
                      {currentRecords.map((payslip, index) =>
                        isMobile ? (
                          <div key={index} className="mb-3">
                            <Card
                              className="shadow-sm w-100"
                              style={{
                                textAlign: "left",
                                borderRadius: "12px",
                                padding: "0.5rem 0.75rem",
                              }}
                            >
                              <Card.Body className="p-3">
                                <div className="d-flex flex-column gap-2">
                                  <div className="d-flex align-items-start mb-2">
                                    <div className="p-2 bg-success bg-opacity-10 rounded-circle me-3 mt-1">
                                      <FiCheckCircle
                                        className="text-success"
                                        size={16}
                                      />
                                    </div>
                                    <div className="flex-grow-1">
                                      <h6
                                        className="fw-bold text-dark mb-1"
                                        style={{ fontSize: "1rem" }}
                                      >
                                        {payslip.name}
                                      </h6>
                                      <small className="text-muted d-block">
                                        {payslip.mobile} • {payslip.employeeId}
                                      </small>
                                      <div className="mt-1">
                                        <small className="text-muted">
                                          {formatDate(payslip.startdate)} - {formatDate(payslip.enddate)} •{" "}
                                          {calculateDaysBetween(payslip.startdate, payslip.enddate)} days
                                        </small>
                                      </div>
                                      {payslip.payslipGeneratedAtRaw && (
                                        <div className="mt-1">
                                          <small className="text-primary fw-medium">
                                            <FiClock
                                              size={10}
                                              className="me-1"
                                            />
                                            Generated: {formatDate(payslip.payslipGeneratedAtRaw)}
                                          </small>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="d-flex align-items-center justify-content-between mt-2">
                                    <span
                                      className="fw-bold"
                                      style={{ fontSize: "1rem" }}
                                    >
                                      {payslip.formattedAmount}
                                    </span>
                                    <div className="d-flex align-items-center gap-2">
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() =>
                                          handleOpenPdfModal(payslip)
                                        }
                                        disabled={!payslip.payslipPdf}
                                        title={
                                          !payslip.payslipPdf
                                            ? "PDF not available"
                                            : "View PDF"
                                        }
                                      >
                                        View
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          </div>
                        ) : (
                          <div key={index} className="col-12">
                            <div className="p-3 bg-light rounded-3 border">
                              <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
                                <div className="d-flex align-items-center mb-2 mb-md-0">
                                  <div className="p-2 bg-success bg-opacity-10 rounded-circle me-3">
                                    <FiCheckCircle
                                      className="text-success"
                                      size={18}
                                    />
                                  </div>
                                  <div>
                                    <h6 className="fw-bold text-dark mb-0">
                                      {payslip.name}
                                    </h6>
                                    <small className="text-muted">
                                      <small className="text-muted">
                                        {payslip.mobile} • {payslip.employeeId} •{" "}
                                        {formatDate(payslip.startdate)} - {formatDate(payslip.enddate)} •{" "}
                                        {calculateDaysBetween(payslip.startdate, payslip.enddate)}{" "}
                                        {calculateDaysBetween(payslip.startdate, payslip.enddate) === 1 ? "day" : "days"}
                                      </small>
                                    </small>
                                    {payslip.payslipGeneratedAtRaw && (
                                      <div className="mt-1">
                                        <small className="text-primary fw-medium">
                                          <FiClock size={12} className="me-1" />
                                          Generated:{" "}
                                          {formatDate(
                                            payslip.payslipGeneratedAtRaw
                                          )}
                                        </small>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="d-flex align-items-center">
                                  <div className="me-3 me-md-4">
                                    <span className="fw-bold">
                                      {payslip.formattedAmount}
                                    </span>
                                  </div>

                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleOpenPdfModal(payslip)}
                                    disabled={!payslip.payslipPdf}
                                    title={
                                      !payslip.payslipPdf
                                        ? "PDF not available"
                                        : "View PDF"
                                    }
                                  >
                                    View Payslip
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>

                    {/* Pagination */}
                    <div
                      className={
                        "d-flex justify-content-between align-items-center"
                      }
                    >
                      <div
                        className="d-flex align-items-center col-5 col-md-auto"
                      >
                        <span className="me-2 text-muted" style={{ fontSize: isMobile ? "0.8rem" : "0.9rem" }}>Paginate</span>
                        <Form.Select
                          size="sm"
                          style={{ width: isMobile ? "70px" : "80px" }}
                          value={recordsPerPage}
                          onChange={(e) => {
                            setRecordsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                        </Form.Select>
                      </div>
                      <div className="col-7 col-md-auto">
                        <Pagination
                          className={isMobile ? "mt-2 flex-wrap justify-content-center" : ""}
                          size={isMobile ? "sm" : ""}
                        >
                          <Pagination.First
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                          />
                          <Pagination.Prev
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          />

                          {Array.from(
                            { length: Math.min(5, totalPages) },
                            (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }

                              return (
                                <Pagination.Item
                                  key={pageNum}
                                  active={pageNum === currentPage}
                                  onClick={() => setCurrentPage(pageNum)}
                                >
                                  {pageNum}
                                </Pagination.Item>
                              );
                            }
                          )}

                          <Pagination.Next
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          />
                          <Pagination.Last
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                          />
                        </Pagination>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 py-sm-5">
                    {loading ? (
                      <Spinner animation="border" variant="primary" />
                    ) : (
                      "No payslips found matching your criteria"
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>

      <Modal
        show={showPdfModal}
        onHide={handleClosePdfModal}
        size="lg"
        centered
        className="pdf-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Payslip</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: "75vh" }}>
          {currentPdfUrl ? (
            <iframe
              src={currentPdfUrl}
              title="Payslip PDF"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          ) : (
            <div className="text-center p-5">
              <FiAlertCircle size={48} className="text-danger mb-3" />
              <h5>PDF not available</h5>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={handleDownloadPdf}>
            Download
          </Button>
          <Button variant="secondary" onClick={handleClosePdfModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Dashboard;